import React, { useEffect, useState } from "react";
import { useTranslate, useNotify } from "react-admin";
import { useMediaQuery, Theme, Button, Typography } from "@mui/material";
import { doc, getDoc, onSnapshot, Timestamp } from "firebase/firestore";
import { getFirestore } from "firebase/firestore";
import { format, formatDistanceToNow } from "date-fns";

import CategoryIcon from "@mui/icons-material/Category";
import CustomerIcon from "@mui/icons-material/Sync";
import TheatersIcon from "@mui/icons-material/Theaters";
import CommentIcon from "@mui/icons-material/Brush";

import InfoCard from "./InfoCard";
import { firebaseApp } from "../../providers/firebase";
import { getAuth } from "firebase/auth";
import ConfirmDialog from "../../layout/Dialog";

const db = getFirestore(firebaseApp);

// Define reusable inline styles
const styles: { [key: string]: React.CSSProperties } = {
  container: {
    padding: "1.5em",
  },
  flex: { display: "flex", gap: "1em" },
  flexColumn: { display: "flex", flexDirection: "column", gap: "1em" },
  leftCol: { flex: 1, marginRight: "0.5em" },
  rightCol: { flex: 1, marginLeft: "0.5em" },
  textContainer: {
    margin: "1em 0",
    padding: "1em",
    background: "#f5f5f5",
    borderRadius: "8px",
  },
  syncButtonContainer: { textAlign: "left", marginTop: "1em" },
};

// Define the types for our Firestore documents
interface SyncCounts {
  showsCount: number;
  categoriesCount: number;
  artistsCount: number;
}

interface SyncLock {
  isLocked: boolean;
  updatedAt: Date | null;
}

/**
 * Custom hook to fetch sync counts from Firestore.
 */
function useSyncCounts(): SyncCounts {
  const [syncCounts, setSyncCounts] = useState<SyncCounts>({
    showsCount: 0,
    categoriesCount: 0,
    artistsCount: 0,
  });

  useEffect(() => {
    const fetchSyncCounts = async () => {
      try {
        const docRef = doc(db, "sync", "dashboard");
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setSyncCounts(docSnap.data() as SyncCounts);
        }
      } catch (error) {
        console.error("Error fetching sync counts:", error);
      }
    };

    fetchSyncCounts();
  }, []);

  return syncCounts;
}

/**
 * Custom hook to subscribe to the sync lock document.
 */
function useSyncLock() {
  const [syncLock, setSyncLock] = useState<SyncLock>({
    isLocked: true,
    updatedAt: null,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const lockDocRef = doc(db, "sync", "syncLock");
    const unsubscribe = onSnapshot(
      lockDocRef,
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();

          let updatedAt: Date | null = null;
          if (data.updatedAt && (data.updatedAt as Timestamp).toDate) {
            updatedAt = (data.updatedAt as Timestamp).toDate();
          } else if (data.updatedAt) {
            updatedAt = new Date(data.updatedAt);
          }
          setSyncLock({
            isLocked: data.isLocked,
            updatedAt,
          });
        } else {
          setSyncLock({
            isLocked: false,
            updatedAt: null,
          });
        }
        setLoading(false);
      },
      (error) => {
        console.error("Error fetching sync lock:", error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  return { syncLock, loading, setSyncLock };
}

/**
 * Dashboard component – displays counts and sync status,
 * and allows the user to trigger a sync.
 */
const Dashboard = () => {
  const isXSmall = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm")
  );
  const isSmall = useMediaQuery((theme: Theme) => theme.breakpoints.down("lg"));
  const translate = useTranslate();
  const notify = useNotify();
  const syncCounts = useSyncCounts();
  const { syncLock, loading, setSyncLock } = useSyncLock();

  // Local state for controlling the confirmation dialog
  const [dialogOpen, setDialogOpen] = useState(false);

  // Helper function to format the last sync day
  const formattedSyncDay = syncLock.updatedAt
    ? format(syncLock.updatedAt, "EEEE")
    : "";

  // Function that performs the sync API call with timeout handling
  const performSync = async () => {
    // Set sync as in-progress
    setSyncLock((prev) => ({
      ...prev,
      isLocked: true,
      updatedAt: new Date(),
    }));
    console.log("Sync started...");

    try {
      const auth = getAuth(firebaseApp);
      const user = auth.currentUser;

      if (!user) {
        throw new Error("User not authenticated. Please log in.");
      }

      const token = await user.getIdToken();

      // Create an AbortController to handle timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        controller.abort();
      }, 5000);

      const response = await fetch(`${import.meta.env.VITE_SYNC_API}/sync`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        console.log("Sync API called successfully");
      } else {
        const errorMessage = `Sync API returned an error: ${response.status}`;
        console.error(errorMessage);
        notify(errorMessage, { type: "error" });
        setSyncLock((prev) => ({
          ...prev,
          isLocked: syncLock.isLocked,
          updatedAt: new Date(),
        }));
      }
    } catch (error: any) {
      if (error.name === "AbortError") {
        // Timeout occurred; as per your requirements, do nothing.
        console.warn("Sync API request timed out, doing nothing.");
      } else {
        console.error("Error calling sync API:", error);
        notify("Error calling sync API: " + error.message, { type: "error" });
        setSyncLock((prev) => ({
          ...prev,
          isLocked: syncLock.isLocked,
          updatedAt: new Date(),
        }));
      }
    }
  };

  // Open the confirmation dialog when the sync button is clicked
  const handleSyncClick = () => {
    setDialogOpen(true);
  };

  // When the user confirms the action, perform the sync and close the dialog
  const handleConfirm = async () => {
    setDialogOpen(false);
    await performSync();
  };

  // Close the dialog without syncing if the user cancels
  const handleCancel = () => {
    setDialogOpen(false);
  };

  // Render InfoCard components based on screen size
  const renderInfoCards = () => {
    if (isXSmall) {
      return (
        <div style={styles.flexColumn}>
          <InfoCard
            value={syncCounts.categoriesCount.toString()}
            icon={CategoryIcon}
            title={translate("pos.dashboard.categories")}
          />
          <InfoCard
            value={formattedSyncDay}
            icon={CustomerIcon}
            title={translate("pos.dashboard.last_sync")}
          />
        </div>
      );
    } else if (isSmall) {
      return (
        <div style={styles.flexColumn}>
          <div style={styles.flex}>
            <InfoCard
              value={syncCounts.categoriesCount.toString()}
              icon={CategoryIcon}
              title={translate("pos.dashboard.categories")}
            />
            <InfoCard
              value={formattedSyncDay}
              icon={CustomerIcon}
              title={translate("pos.dashboard.last_sync")}
            />
          </div>
        </div>
      );
    } else {
      return (
        <div style={styles.flex}>
          <div style={styles.leftCol}>
            <div style={styles.flex}>
              <InfoCard
                value={syncCounts.categoriesCount.toString()}
                icon={CategoryIcon}
                title={translate("pos.dashboard.categories")}
              />
              <InfoCard
                value={syncCounts.showsCount.toString()}
                icon={TheatersIcon}
                title={translate("pos.dashboard.shows")}
              />
            </div>
          </div>
          <div style={styles.rightCol}>
            <div style={styles.flex}>
              <InfoCard
                value={syncCounts.artistsCount.toString()}
                icon={CommentIcon}
                title={translate("pos.dashboard.artists")}
              />
              <InfoCard
                value={formattedSyncDay}
                icon={CustomerIcon}
                title={translate("pos.dashboard.last_sync")}
              />
            </div>
          </div>
        </div>
      );
    }
  };

  return (
    <div style={styles.container}>
      {renderInfoCards()}

      <div style={styles.textContainer}>
        <Typography
          variant="body1"
          dangerouslySetInnerHTML={{
            __html: translate("pos.dashboard.sync_message"),
          }}
        />
      </div>

      <div style={styles.syncButtonContainer}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSyncClick}
          disabled={syncLock.isLocked || loading}
        >
          {loading
            ? translate("pos.dashboard.loading")
            : syncLock.isLocked
            ? translate("pos.dashboard.syncing")
            : translate("pos.dashboard.sync_now")}
        </Button>
        {syncLock.updatedAt && (
          <Typography variant="caption" style={{ marginLeft: "1em" }}>
            Last sync:{" "}
            {formatDistanceToNow(syncLock.updatedAt, { addSuffix: true })}
          </Typography>
        )}
      </div>

      {/* Confirmation Dialog */}
      <ConfirmDialog
        open={dialogOpen}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        title={translate("pos.dialog.synchronization")}
        description={translate("pos.dialog.description")}
        cancelText={translate("pos.dialog.cancel")}
        confirmText={translate("pos.dialog.sync")}
      />
    </div>
  );
};

export default Dashboard;
